import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

export default function FreeAEMConsultation() {
  const canonical = 'https://divaisetech.com/free-aem-consultation';
  const servicePageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Free AEM Consultation',
    description: 'Free consultation for Adobe Experience Manager implementation, migration, and optimization planning.',
    url: canonical,
    provider: {
      '@type': 'Organization',
      name: 'DivaiseTech',
      url: 'https://divaisetech.com',
      logo: 'https://divaisetech.com/logo.png',
    },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    serviceType: 'Adobe Experience Manager Consultation',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
    },
  };

  const consultationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Free AEM Consultation',
    description: 'Get a free 1-hour AEM consultation from Adobe Experience Manager experts. Discuss implementation, migration, optimization, and strategy.',
    url: canonical,
    serviceType: 'Adobe Experience Manager Consultation',
    provider: {
      '@type': 'Organization',
      name: 'Divaise Technology Private Limited',
      url: 'https://divaisetech.com',
      email: 'consult@divaisetech.com',
      telephone: '+91-8287934973',
    },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
  };

  return (
    <div>
      <SEO
        title="Free AEM Consultation | Adobe Experience Manager Expert Review"
        description="Get a free 1-hour AEM consultation from Adobe Experience Manager experts. Discuss implementation, migration, optimization, and strategy."
        keywords="AEM consultation, Adobe Experience Manager review, AEM strategy, AEM assessment, free consultation"
        canonical={canonical}
        schema={[consultationSchema, servicePageSchema]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Free AEM Expert Consultation</h1>
          <p className="text-xl text-slate-200 mb-8">
            Get actionable insights from our Adobe Experience Manager experts. Discuss your AEM goals, challenges, and vision in a 1-hour strategic consultation. Completely free, no obligation.
          </p>
        </div>
      </section>

      {/* Consultation Contact Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-10 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Book Your Free Consultation</h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with our AEM experts directly to schedule your free 1-hour consultation.
            </p>

            <div className="space-y-4 mb-8">
              <a
                href="mailto:consult@divaisetech.com"
                className="w-full inline-flex items-center justify-center bg-accent text-black px-6 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all"
              >
                Email: consult@divaisetech.com
              </a>
              <a
                href="tel:+918287934973"
                className="w-full inline-flex items-center justify-center border-2 border-gray-900 text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all"
              >
                Call: +91-8287934973
              </a>
            </div>

            <p className="text-sm text-gray-600 mb-8">
              Our AEM team typically responds within 24 hours.
            </p>

            <Link
              to="/contact"
              className="inline-flex items-center bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-black transition-all"
            >
              Go to Contact Page <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-white">What's Included in Your Free Consultation</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'AEM Assessment',
                desc: 'Review of your current situation, goals, and challenges',
              },
              {
                title: 'Expert Recommendations',
                desc: 'Specific suggestions tailored to your business needs',
              },
              {
                title: 'Implementation Strategy',
                desc: 'Roadmap and approach for your AEM initiative',
              },
              {
                title: 'Timeline & Budget Estimate',
                desc: 'Realistic expectations for cost and duration',
              },
              {
                title: 'Risk & Opportunity Analysis',
                desc: 'Key considerations and quick wins identified',
              },
              {
                title: 'Next Steps',
                desc: 'Clear action items and decision points outlined',
              },
            ].map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <CheckCircle className="w-6 h-6 text-accent mb-3" />
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consultant Profiles Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-white">Meet Your AEM Consultants</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Aditya Sharma',
                title: 'AEM Architect & Implementation Lead',
                expertise: '8 years AEM experience, Adobe Certified Expert, led 20+ implementations',
              },
              {
                name: 'Priya Patel',
                title: 'AEM Solutions Consultant',
                expertise: '6 years AEM background, Adobe Launch & Analytics specialist',
              },
              {
                name: 'Rajesh Kumar',
                title: 'Cloud Services Specialist',
                expertise: '5 years AEM Cloud expertise, Migration certified',
              },
            ].map((consultant, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-gray-200 p-6 text-center">
                <div className="w-16 h-16 bg-accent rounded-full mx-auto mb-4 flex items-center justify-center text-white font-bold text-xl">
                  {consultant.name.split(' ').map(n => n[0]).join('')}
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{consultant.name}</h3>
                <p className="text-sm font-semibold text-accent mb-3">{consultant.title}</p>
                <p className="text-sm text-gray-600">{consultant.expertise}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-white">Why Get Consultation from Divaise?</h2>
          <div className="space-y-6">
            {[
              {
                title: 'Adobe Certified Experts',
                desc: 'Our team includes Adobe AEM Certified Developers and Solutions Consultants',
              },
              {
                title: '20+ Years Combined AEM Experience',
                desc: 'Deep expertise across AEM 6.x, AEM Cloud, and enterprise implementations',
              },
              {
                title: 'No Sales Pitch',
                desc: 'Honest, unbiased recommendations focused on your success, not our commissions',
              },
              {
                title: 'Global Perspective',
                desc: 'Experience across industries and geographies gives us actionable insights',
              },
              {
                title: 'Proven Track Record',
                desc: '25+ successful AEM implementations with measurable business impact',
              },
              {
                title: 'Completely Free',
                desc: 'No fees, no commitments, no hidden costs for your 1-hour consultation',
              },
            ].map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-8 border border-gray-200 flex gap-6">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-r from-accent to-accent/80 text-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Digital Experience?</h2>
          <p className="text-xl mb-8 text-black/80">
            Book your free AEM strategy consultation today. Let's discuss your vision and explore how AEM can power your digital growth.
          </p>
          <Link
            to="/free-aem-consultation"
            className="inline-flex items-center bg-black text-white px-8 py-4 rounded-lg font-semibold hover:bg-black/90 transition-all"
          >
            Schedule Consultation <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-white">FAQs about Our Consultation</h2>
          <div className="space-y-6">
            {[
              {
                q: 'Is the consultation really free?',
                a: 'Yes, completely free. No credit card required. We offer this as a way to understand your needs and show our expertise.',
              },
              {
                q: 'How long is the consultation call?',
                a: 'The consultation is 1 hour long. We focus on understanding your goals, challenges, and providing actionable recommendations.',
              },
              {
                q: 'Do you require any NDA or agreements?',
                a: 'No. The consultation is informal and exploratory. If you ultimately want to work together, we can discuss terms at that time.',
              },
              {
                q: 'What if I decide not to hire Divaise?',
                a: 'No problem. Our goal is to provide genuine value in the consultation itself. We hope you find it helpful regardless of future decisions.',
              },
            ].map((item, idx) => (
              <details key={idx} className="group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer hover:shadow-lg transition-shadow">
                <summary className="flex justify-between items-center font-semibold text-gray-900">
                  {item.q}
                  <span className="text-accent">+</span>
                </summary>
                <p className="mt-4 text-gray-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
